<template>
  <div class="modal">
    <div class="modal-header">
      <div class="modal-title">予約配信設定</div>
      <div class="modal-close-button-wrapper">
        <Button class="modal-close-button" @click="closeModal()">
          <v-ons-icon icon="fa-close" class="modal-close-icon" />
        </Button>
      </div>
    </div>
    <div class="modal-body">
      <!-- 時間 -->
      <div class="input-form-area">
        <div class="input-form">
          <label class="form-title" for="streaming-start-time">開始時刻</label><br />
          <input
            type="time"
            name="streaming-start-time"
            id="streaming-start-time"
            class="input-time"
            v-model="streamingStartTime"
          />
        </div>
        <label class="between-time">〜</label>
        <div class="input-form">
          <label class="form-title" for="streaming-end-time">終了時刻</label><br />
          <input
            type="time"
            name="streaming-end-time"
            id="streaming-end-time"
            class="input-time"
            v-model="streamingEndTime"
          />
        </div>
      </div>
      <!-- 曜日 -->
      <div class="input-form select-days-of-week-form">
        <p class="form-title">配信曜日</p>
        <div class="day-of-week-button-area">
          <button
            v-for="(selectableDayOfWeek, index) in SELECTABLE_DAYS_OF_WEEK"
            :key="index"
            :class="[
              'day-of-week-button',
              { 'is-selected': isActiveDayOfWeekButton(selectableDayOfWeek) },
            ]"
            @click="dayOfWeekButtonClick(selectableDayOfWeek)"
          >
            {{ selectableDayOfWeek }}
          </button>
        </div>
      </div>
    </div>
    <div class="modal-footer">
      <SmallButton
        title="保存"
        :disabled="!isFormValid"
        @click="onClickStreamGroupSchedulingSubmitButton()"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';

import SmallButton from '@/components/common/SmallButton.vue';
import Button from '@/components/common/Button.vue';
import { postStreamGroupSchedule } from '@/composable/data_push_common';

/** propsの受け取り */
const props = defineProps({
  selectedStreamGroupId: Number,
  getStreamGroup: Function,
  closeModal: Function,
});

// 配信開始時間
const streamingStartTime = ref('');
// 配信終了時間
const streamingEndTime = ref('');
// 選択済曜日リスト
const selectedDaysOfWeek = ref([]);
// 選択可能曜日リスト
const SELECTABLE_DAYS_OF_WEEK = ['月', '火', '水', '木', '金', '土', '日'];
// axios処理待ち
const isAxiosWait = ref(false);

/**
 * 選択済曜日リストに引数の曜日が入っているか判定
 */
const isActiveDayOfWeekButton = (dayOfWeekName) => {
  return selectedDaysOfWeek.value.includes(dayOfWeekName);
};

/**
 * フォーム入力情報が全て正しいか判定
 */
const isFormValid = computed(() => {
  const [streamingStartTimeHour = '', streamingStartTimeMinute = ''] =
    streamingStartTime.value.split(':');
  const [streamingEndTimeHour = '', streamingEndTimeMinute = ''] =
    streamingEndTime.value.split(':');

  return (
    streamingStartTime.value !== '' &&
    streamingEndTime.value !== '' &&
    selectedDaysOfWeek.value.length &&
    (streamingStartTimeHour < streamingEndTimeHour ||
      (streamingStartTimeHour === streamingEndTimeHour &&
        streamingStartTimeMinute < streamingEndTimeMinute))
  );
});

/**
 * 選択した曜日が選択済曜日リストに含まれていれば削除し、含まれていなければ追加する
 * @param {*} selectedDayOfWeek 選択した曜日
 */
const dayOfWeekButtonClick = (selectedDayOfWeek) => {
  if (selectedDaysOfWeek.value.includes(selectedDayOfWeek)) {
    selectedDaysOfWeek.value = selectedDaysOfWeek.value.filter(
      (dayOfWeek) => dayOfWeek !== selectedDayOfWeek
    );
  } else {
    selectedDaysOfWeek.value.push(selectedDayOfWeek);
  }
};

/**
 * 入力した配信予約情報をサーバに送信する
 */
const onClickStreamGroupSchedulingSubmitButton = () => {
  // 入力データが意図していない中身もしくは通信処理中の場合は処理中止
  if (!isFormValid.value || !props.selectedStreamGroupId || isAxiosWait.value) return;

  isAxiosWait.value = true;
  // 配信予約情報登録
  postStreamGroupSchedule(
    props.selectedStreamGroupId,
    selectedDaysOfWeek.value,
    streamingStartTime.value,
    streamingEndTime.value
  )
    .then(() => {
      props.getStreamGroup();
      props.closeModal();
    })
    .catch(() => {}) // POST実行時に400エラーの場合にエラー画面を表示しないようにする
    .finally(() => {
      isAxiosWait.value = false;
    });
};

/**
 * モーダルウィンドウを閉じる
 */
const closeModal = () => {
  props.closeModal();
};
</script>

<style scoped>
/** モーダル */
.modal {
  position: relative;
  height: auto;
  height: 20rem;
  width: 80%;
  margin: auto;
  color: #222;
  background: #fff;
  border-radius: 10px;
  overflow: scroll;
  white-space: normal;
}

/** モーダルヘッダ */
.modal-header {
  position: sticky;
  top: 0;
  left: 0;
  right: 0;
  border-bottom: 1px #ddd solid;
  font-weight: bold;
}
.modal-title {
  font-size: 1.8rem;
  padding-block: 1rem;
}

/** モーダルクローズボタン */
.modal-close-button-wrapper {
  position: absolute;
  top: 1rem;
  right: 1rem;
}
.modal-close-button,
.modal-close-button:active {
  background: #fff;
  color: #aaa;
  height: 2.5rem;
  width: 2.5rem;
  padding: 0;
}
.modal-close-icon {
  font-size: 2.5rem;
  height: 2.5rem;
  width: 2.5rem;
  line-height: 2.5rem;
}

/** モーダルボディ */
.modal-body {
  width: 80%;
  margin: auto;
  padding-top: 3rem;
  text-align: left;
}

/** フォーム共通パーツ */
.input-form-area {
  display: flex;
  align-items: end;
}
.input-form {
  margin-bottom: 2.5rem;
  width: 50%;
  min-width: 10rem;
}
.form-title {
  margin: 0;
  font-size: 1.4rem;
  font-weight: bold;
}

.input-time {
  background-color: white;
  color: black;
  font-size: 1.6rem;
  font-weight: bold;
  border-radius: 1rem;
  border: solid 1px #cccccc;
  height: 5rem;
  width: 100%;
  min-width: 10rem;
  position: relative;
  text-align: center;
  margin-top: 1rem;
  appearance: none;
  -webkit-appearance: none;
  box-sizing: border-box;
}
.input-time::-webkit-calendar-picker-indicator {
  position: absolute;
  opacity: 0;
}
.between-time {
  padding: 0 1rem;
  color: black;
  font-size: 1.4rem;
  font-weight: 700;
  line-height: 5rem;
  margin-bottom: 2.5rem;
}

/** 曜日選択エリア */
.day-of-week-button-area {
  display: flex;
  padding-block: 1rem;
  justify-content: space-between;
  max-width: 40rem;
}
.select-days-of-week-form {
  width: 100%;
}

.day-of-week-button {
  color: #aaaaaa;
  border: solid 1px #dddddd;
  background-color: #ffffff;
  height: 3rem;
  width: 3rem;
  padding: 0;
  font-size: 1.4rem;
  border-radius: 50%;
  font-weight: 400;
  text-align: center;
  line-height: 1.2rem;
  flex-shrink: 0;
}
.day-of-week-button.is-selected {
  color: #ffffff;
  background-color: #21963b;
  border: solid 1px #21963b;
  font-weight: 700;
}

/** モーダルフッタ */
.modal-footer {
  padding: 1.5rem 2rem;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  box-shadow: 0px 0px 4px rgba(0, 0, 0, 0.25);
}
</style>
